import { DesktopWindows, FilterList } from '@mui/icons-material';
import { CircularProgress, Typography } from '@mui/material';
import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { ServiceFilterModal } from './fragments/ServiceFilterModal';
import { ServiceListFilters } from './fragments/ServiceListFilters';
import { ServiceListSkeleton, ServiceListError } from './fragments/ServiceListStates';
import { ServiceSection } from './fragments/ServiceSection';
import { ServiceSortBar } from './fragments/ServiceSortBar';
import { ServiceStatsOverview } from './fragments/ServiceStatsOverview';
import {
  PageContainer,
  PageHeader,
  HeaderTop,
  PageTitle,
  HeaderActions,
  HeaderButton,
  FilterBadge,
  ContentCurve,
  ServicesList,
  PullIndicator,
  ActiveFiltersDisplay,
  ActiveFilterChip,
  ActiveFilterChipClose,
  ScrollSentinel,
  LoadingMore,
} from './MobileServices.styles';
import { getMobileColumnSelection } from './MobileServices.utils';
import { useMobileServiceFilters } from './useMobileServiceFilters';
import { useMobileServiceQueries } from './useMobileServiceQueries';
import { useMultiInfiniteScroll } from './useMultiInfiniteScroll';
import { useGetCurrentUser } from '../../../queries/user-query';
import { useDebouncedValue } from '../../../utils/use-debounced-value';
import { usePullToRefresh } from '../../../utils/use-pull-to-refresh';

const mobileColumnSelectionItems = getMobileColumnSelection();

export const MobileServices: React.FC = () => {
  const navigate = useNavigate();
  const filters = useMobileServiceFilters();
  const debouncedSearch = useDebouncedValue(filters.searchQuery, 400);

  const { data: currentUser, isLoading: isLoadingUser } = useGetCurrentUser();

  const queryOptions = useMemo(
    () => ({
      filterModel: {
        items: [] as Array<{ field: string; operator: string; value: string | string[] }>,
      },
      pageSize: 20,
      sortModel: [{ field: 'scheduledDate', sort: 'asc' }],
      multiSearchQuery: debouncedSearch || undefined,
    }),
    [debouncedSearch]
  );

  const clientFilters = useMemo(
    () => ({
      urgentFilter: filters.urgentFilter,
      todayFilter: filters.todayFilter,
      activeSort: filters.activeSort,
      serviceFilter: filters.serviceFilter,
    }),
    [filters.urgentFilter, filters.todayFilter, filters.activeSort, filters.serviceFilter]
  );

  const queries = useMobileServiceQueries(queryOptions, mobileColumnSelectionItems, clientFilters);

  const { sentinelRef, hasAnyNextPage, isAnyFetchingNextPage } = useMultiInfiniteScroll({
    queries: [queries.allocated, queries.depot, queries.zones],
  });

  const currentUserName = useMemo(
    () => (currentUser ? `${currentUser.firstName} ${currentUser.lastName}`.trim() : ''),
    [currentUser]
  );

  const handleCardClick = useCallback(
    (serviceId: number | undefined, logoPic?: string) => {
      if (serviceId) navigate(`/mobile/services/${serviceId}`, { state: { logoPic } });
    },
    [navigate]
  );

  const isLoading = isLoadingUser || queries.isLoading;

  const { containerRef, pullDistance, isRefreshing } = usePullToRefresh({
    onRefresh: () => queries.refetchAll(),
  });

  // Determine which sections to show based on stat filter
  const showAllocated = !filters.activeStatFilter || filters.activeStatFilter === 'allocated';
  const showDepot = !filters.activeStatFilter || filters.activeStatFilter === 'depot';
  const showZones = !filters.activeStatFilter || filters.activeStatFilter === 'zones';

  return (
    <PageContainer>
      <PageHeader>
        <HeaderTop>
          <PageTitle>Services</PageTitle>
          <HeaderActions>
            <HeaderButton onClick={() => navigate('/services')} aria-label="Desktop view">
              <DesktopWindows />
            </HeaderButton>
            <HeaderButton
              onClick={() => filters.setIsFilterModalOpen(true)}
              aria-label="Open filters"
            >
              <FilterList />
              {filters.activeFilterCount > 0 && (
                <FilterBadge>{filters.activeFilterCount}</FilterBadge>
              )}
            </HeaderButton>
          </HeaderActions>
        </HeaderTop>
      </PageHeader>

      <ServiceStatsOverview
        allocatedToMe={queries.allocated.count}
        myDepot={queries.depot.count}
        myZones={queries.zones.count}
        onStatClick={filters.handleStatClick}
        activeFilter={filters.activeStatFilter}
      />

      <ContentCurve ref={containerRef} sx={{ overflowY: 'auto', flex: 1 }}>
        <PullIndicator
          sx={{
            height:
              pullDistance > 0 || isRefreshing
                ? `${Math.max(pullDistance, isRefreshing ? 40 : 0)}px`
                : 0,
          }}
        >
          {isRefreshing ? (
            <CircularProgress size={20} />
          ) : pullDistance > 40 ? (
            'Release to refresh'
          ) : pullDistance > 0 ? (
            'Pull to refresh'
          ) : null}
        </PullIndicator>
        <ServiceListFilters
          searchQuery={filters.searchQuery}
          onSearchChange={filters.setSearchQuery}
        />
        <ServiceSortBar activeSort={filters.activeSort} onSortChange={filters.setActiveSort} />

        {filters.activeFilterChips.length > 0 && (
          <ActiveFiltersDisplay>
            {filters.activeFilterChips.map((chip) => (
              <ActiveFilterChip key={chip.key}>
                {chip.label}
                <ActiveFilterChipClose
                  onClick={() => filters.handleRemoveFilterChip(chip.key)}
                  aria-label={`Remove ${chip.label} filter`}
                >
                  ✕
                </ActiveFilterChipClose>
              </ActiveFilterChip>
            ))}
          </ActiveFiltersDisplay>
        )}

        {queries.isError ? (
          <ServiceListError onRetry={() => queries.refetchAll()} />
        ) : isLoading ? (
          <ServiceListSkeleton />
        ) : (
          <ServicesList>
            {showAllocated && (
              <ServiceSection
                title="Allocated To Me"
                services={queries.allocated.services}
                emptyMessage="No services allocated to you"
                currentUserName={currentUserName}
                onCardClick={handleCardClick}
              />
            )}
            {showDepot && (
              <ServiceSection
                title="My Depot"
                services={queries.depot.services}
                emptyMessage="No depot services"
                currentUserName={currentUserName}
                onCardClick={handleCardClick}
              />
            )}
            {showZones && (
              <ServiceSection
                title="My Zones"
                services={queries.zones.services}
                emptyMessage="No zone services"
                currentUserName={currentUserName}
                onCardClick={handleCardClick}
              />
            )}
            {isAnyFetchingNextPage && (
              <LoadingMore>
                <CircularProgress size={16} />
                <Typography variant="body2">Loading more services...</Typography>
              </LoadingMore>
            )}
            {hasAnyNextPage && <ScrollSentinel ref={sentinelRef} />}
          </ServicesList>
        )}
      </ContentCurve>

      <ServiceFilterModal
        open={filters.isFilterModalOpen}
        onClose={() => filters.setIsFilterModalOpen(false)}
        currentFilters={{
          serviceType: filters.serviceFilter,
          dateRange: filters.dateRangeFilter,
          urgent: filters.urgentFilter,
          today: filters.todayFilter,
        }}
        onApply={filters.handleApplyFilters}
        onClearAll={filters.handleClearAllFilters}
      />
    </PageContainer>
  );
};
